import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {map, shareReplay} from "rxjs/operators";
import {sendQuery} from "../../../../../common/utils";
import {
  Berth,
  BerthVisit,
  ShorePower,
  ShorePowerDetails,
  ShorePowerExemption,
  ShorePowerStatus,
  Terminal
} from "@portbase/bezoekschip-service-typescriptmodels";
import {VisitContext} from "../../../../visit-context";
import {firstValueFrom, Observable, of} from "rxjs";
import {AppContext} from "../../../../../app-context";
import {isEqual} from "lodash";
import {
  HOLLAND_AMERIKA_KADE_BOX_SUPPLIER,
  ITRANSACT_BOX_SUPPLIER
} from "./shore-power.constants";

@Component({
  selector: 'app-shore-power',
  templateUrl: './shore-power.component.html',
  styleUrls: ['./shore-power.component.css'],
  standalone: false
})
export class ShorePowerComponent implements OnChanges {

  visitContext = VisitContext;
  appContext = AppContext;

  @Input() berth: Berth;
  @Input() berthVisit: BerthVisit;

  shorePower: ShorePower;
  preservedShorePower : ShorePower;
  preservedShorePowerDetails : ShorePowerDetails;
  preservedBerth: Berth;

  shorePowerEnabledOnTerminalRefData$!: Observable<boolean>;
  boxSupplier$!: Observable<string>;
  // Single resolved terminal the template consumes for BOTH the outer shore-power gate
  // and the provider switch, so rendering never depends on the asynchronously-written
  // chosenBoxSupplier field (see ngOnChanges).
  terminal$!: Observable<Terminal>;
  pinCodeReceived: boolean;
  showPinCode: boolean;
  pinCodePlaceholder: string;

  // Box-supplier value chosen on the terminal; drives the provider section switch
  // and the boxSupplier written into the command.
  private chosenBoxSupplier: string = null;

  readonly itransactBoxSupplier = ITRANSACT_BOX_SUPPLIER;
  readonly hollandAmerikaKadeBoxSupplier = HOLLAND_AMERIKA_KADE_BOX_SUPPLIER;

  readonly shorePowerExemptions: ShorePowerExemption[] = [
    ShorePowerExemption.UNAVAILABLE,
    ShorePowerExemption.OPERATIONS,
    ShorePowerExemption.EMERGENCY,
    ShorePowerExemption.EXCEPTIONAL_CIRCUMSTANCES,
    ShorePowerExemption.ZERO_EMISSION,
    ShorePowerExemption.SHORT_TERM_BERTHING,
    ShorePowerExemption.OTHER
  ];

  exemptionFormatter = (value: ShorePowerExemption): string => {
    switch (value) {
      case ShorePowerExemption.UNAVAILABLE:
        return 'Shore power unavailable or not operational';
      case ShorePowerExemption.OPERATIONS:
        return 'Operations';
      case ShorePowerExemption.EMERGENCY:
        return 'Emergency';
      case ShorePowerExemption.EXCEPTIONAL_CIRCUMSTANCES:
        return 'Exceptional circumstances';
      case ShorePowerExemption.ZERO_EMISSION:
        return 'Zero-emission';
      case ShorePowerExemption.SHORT_TERM_BERTHING:
        return 'Short-term berthing';
      case ShorePowerExemption.OTHER:
        return 'Other';
      default:
        return '';
    }
  };

  exemptionInfoText = (value: ShorePowerExemption): string => {
    switch (value) {
      case ShorePowerExemption.UNAVAILABLE:
        return 'The shore power connection is unavailable or not operational. This refers to the shore-side installation.';
      case ShorePowerExemption.OPERATIONS:
        return 'The berth is used exclusively for bunkering, taking on supplies, or carrying out repairs to the vessel or its equipment.';
      case ShorePowerExemption.EMERGENCY:
        return 'The vessel must berth because it requires assistance or is in distress; needs to generate power for a limited period due to an emergency posing an immediate risk to human life, the vessel, or the environment; or is affected by another situation of force majeure. This also applies if unforeseen circumstances outside the vessel require an unscheduled, non-routine port call for safety reasons or a rescue operation at sea.';
      case ShorePowerExemption.EXCEPTIONAL_CIRCUMSTANCES:
        return 'Seeking shelter due to severe weather or berthing as required for search and rescue operations.';
      case ShorePowerExemption.ZERO_EMISSION:
        return 'The vessel can berth without producing emissions or causing noise.';
      case ShorePowerExemption.SHORT_TERM_BERTHING:
        return 'The vessel will be berthed for less than two hours.';
      case ShorePowerExemption.OTHER:
        return 'If none of the exemptions above apply, but the vessel still needs to berth at the Holland Amerikakade, select \u201cOther\u201d and explain why shore power cannot be used.';
      default:
        return '';
    }
  };

  get isHollandAmerikaKade(): boolean {
    return this.chosenBoxSupplier === HOLLAND_AMERIKA_KADE_BOX_SUPPLIER;
  }

  get isItransact(): boolean {
    return this.chosenBoxSupplier === ITRANSACT_BOX_SUPPLIER;
  }

  get showForm29Banner(): boolean {
    return this.shorePower?.exemption === ShorePowerExemption.OTHER;
  }

  async getShorePowerEnabledOnTerminal(): Promise<boolean> {
    return await firstValueFrom(
      this.shorePowerEnabledOnTerminalRefData$
    );
  }

  getTerminal: () => Observable<Terminal> =
    () => {
      return sendQuery('com.portbase.bezoekschip.common.api.visit.GetTerminal', {
        portUnCode: this.visitContext.visit.portOfCall.port.locationUnCode,
        terminalCode: this.berthVisit.berth.terminalCode
      });
    }

  private setShorePowerToChosenTerminal() {
    if (this.berthVisit.berth === null) {
      this.chosenBoxSupplier = null;
      this.terminal$ = of(null);
      this.shorePowerEnabledOnTerminalRefData$ = of(false);
      this.boxSupplier$ = of(null);
      return;
    }
    this.terminal$ = this.getTerminal().pipe(shareReplay(1));
    this.shorePowerEnabledOnTerminalRefData$ = this.terminal$.pipe(map((terminal: Terminal) => terminal.shorePower));
    this.boxSupplier$ = this.terminal$.pipe(map((terminal: Terminal) => {
      this.chosenBoxSupplier = terminal.boxSupplier;
      return terminal.boxSupplier;
    }));
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['berthVisit']) {
      this.setShorePowerToChosenTerminal();
    }
    if (changes['berth']) {
      this.setShorePowerToChosenTerminal();
      await this.setShorePowerUnderBerthVisit();
    }
    this.chosenBoxSupplier = this.berthVisit.berth === null ? null
      : await firstValueFrom(this.boxSupplier$);
    this.ensureBoxSupplierForHollandAmerikaKade();
    this.pinCodeReceived = (this.shorePower?.pinCode != null)
    this.showPinCode = this.pinCodeReceived && this.isSameAgent();
    this.setPinCodePlaceholder();
  }

  async setShorePowerUnderBerthVisit() {
    if (!(await this.getShorePowerEnabledOnTerminal())) {
      this.shorePower = null;
    } else {
      if (this.berthVisit.shorePower) {
        if (this.shorePower == null) {
          this.shorePower = {...this.berthVisit.shorePower}
          this.shorePower.shorePowerDetails = {...this.berthVisit.shorePower.shorePowerDetails}
          this.preservedShorePower = {...this.shorePower}
          this.preservedShorePowerDetails = {...this.shorePower.shorePowerDetails}
          this.preservedBerth = {...this.berth}
        } else if (this.preservedBerth &&
                   this.preservedBerth.code === this.berth.code) {
          this.shorePower = {...this.preservedShorePower}
          this.shorePower.shorePowerDetails = {...this.preservedShorePowerDetails}
        } else {
          this.shorePower = {...this.newShorePower()};
        }
      } else if (this.berthVisit.shorePower == null) {
        this.shorePower = {...this.newShorePower()}
      }
    }
    if (this.berth !== null) {
      this.berthVisit.shorePower = this.shorePower;
    }
  }

  shorePowerDetailsPreserved(): boolean {
    return this.preservedShorePowerDetails != null &&
           Object.keys(this.preservedShorePowerDetails).length > 0;
  }

  berthPreserved(): boolean {
    return this.preservedBerth != null;
  }

  newShorePower(): ShorePower {
    return {
      shorePowerEnabled: null,
      shorePowerDetails: null,
      pinCode: null
    }
  }

  newShorePowerDetails(): ShorePowerDetails {
    return {
      organisation: this.appContext.userProfile.organisation,
      boxSupplier: this.chosenBoxSupplier
    };
  }

  /**
   * The Holland Amerikakade flow records only the Yes/No + (when No) the exemption,
   * but the backend still keys the mandatory rule off the box supplier carried on
   * shorePowerDetails. Make sure that value is present even when the agent answers No
   * (where the ITransact detail block would normally stay null).
   */
  private ensureBoxSupplierForHollandAmerikaKade() {
    if (this.isHollandAmerikaKade && this.shorePower) {
      if (!this.shorePower.shorePowerDetails) {
        this.shorePower.shorePowerDetails = {
          organisation: this.appContext.userProfile.organisation,
          boxSupplier: this.chosenBoxSupplier
        };
      } else {
        this.shorePower.shorePowerDetails.boxSupplier = this.chosenBoxSupplier;
      }
    }
  }

  /**
   * When the agent switches the Yes/No for a Holland Amerikakade berth, keep the
   * box supplier populated (so the backend still recognises the HAK flow) and clear
   * the exemption whenever shore power is requested (Yes) since it no longer applies.
   */
  onHollandAmerikaKadeShorePowerEnabledChange(enabled: boolean) {
    if (enabled) {
      this.shorePower.exemption = null;
    }
    this.ensureBoxSupplierForHollandAmerikaKade();
  }

  resetShorePowerDetails(enabled: boolean) {
    if (enabled) {
      if (this.shorePowerDetailsPreserved() &&
          this.berthPreserved() &&
          this.preservedBerth.code === this.berth.code) {
        this.shorePower.shorePowerDetails = {...this.preservedShorePowerDetails};
      } else {
        this.shorePower.shorePowerDetails = {...this.newShorePowerDetails()};
      }
    } else {
      this.shorePower.shorePowerDetails = null;
    }
  }

  isSameAgent() {
    return this.appContext.isAdmin() || isEqual(this.appContext.userProfile.organisation.shortName, this.shorePower?.shorePowerDetails?.organisation?.shortName);
  }

  setPinCodePlaceholder() {
    if (ShorePowerStatus.TECHNICAL_ERROR === this.shorePower?.shorePowerStatus) {
      this.pinCodePlaceholder = 'Technical error';
    }
    else if (!this.pinCodeReceived) {
      this.pinCodePlaceholder = 'Will be provided upon ATA confirmation';
    } else if (!this.showPinCode) {
      this.pinCodePlaceholder = 'Visible only to the requesting organisation';
    } else {
      this.pinCodePlaceholder = '';
    }
  }

  selectPinCode(event: MouseEvent) {
    const element = event.target as HTMLElement;

    const range = document.createRange();
    range.selectNodeContents(element);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
}
